import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building, Shield, Key, Loader2, Phone, CreditCard } from 'lucide-react';
import { useAuth } from '@snipit/core/context/AuthContext';
import { RecaptchaVerifier, deleteUser } from 'firebase/auth';
import { auth, db } from '@snipit/core/services/firebase';
import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import OptimizedImage from '@snipit/ui/components/OptimizedImage';
import BillingView from './BillingView';
import AdminView from './AdminView';

export default function SettingsView() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const { userProfile, updateProfile, currentUser, startMfaEnrollment, completeMfaEnrollment } = useAuth();
  const { organization: enterpriseOrg } = useOrganization();
  
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  
  // MFA State
  const [enrollingMfa, setEnrollingMfa] = useState(false);
  const [mfaPhone, setMfaPhone] = useState('');
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaSmsCode, setMfaSmsCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  // Clean up recaptcha on unmount
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifierEnroll) {
        window.recaptchaVerifierEnroll.clear();
        window.recaptchaVerifierEnroll = null;
      }
    };
  }, []);
  
  useEffect(() => {
    if (userProfile) {
      setFullName(userProfile.displayName || '');
      setBio(userProfile.bio || '');
      setOrganization(userProfile.organization || '');
      setRole(userProfile.role || '');
      setPhotoURL(userProfile.photoURL || '');
    }
  }, [userProfile]);

  const handleSaveAccount = async () => {
    try {
      await updateProfile({
        displayName: fullName,
        bio,
        organization,
        role,
        photoURL
      });
      alert('Profile updated successfully!');
    } catch (error) {
      alert('Failed to update profile.');
    }
  };

  const handleStartMfa = async () => {
    if (!mfaPhone || mfaPhone.length < 10) {
      setMfaError('Please enter a valid phone number with country code (e.g. +1234567890)');
      return;
    }
    setMfaError('');
    setMfaLoading(true);
    try {
      if (!window.recaptchaVerifierEnroll) {
        window.recaptchaVerifierEnroll = new RecaptchaVerifier(auth, 'mfa-enroll-button', {
          size: 'invisible'
        });
      }
      const vId = await startMfaEnrollment(mfaPhone, window.recaptchaVerifierEnroll);
      setMfaVerificationId(vId);
    } catch (err) {
      console.error(err);
      setMfaError('Failed to send verification code. ' + err.message);
      if (window.recaptchaVerifierEnroll) {
        window.recaptchaVerifierEnroll.clear();
        window.recaptchaVerifierEnroll = null;
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const handleCompleteMfa = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      await completeMfaEnrollment(mfaVerificationId, mfaSmsCode, 'Primary Phone');
      setEnrollingMfa(false);
      setMfaVerificationId('');
      setMfaPhone('');
      setMfaSmsCode('');
    } catch (err) {
      console.error(err);
      setMfaError('Invalid verification code.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      if (!currentUser) return;
      const snippetsRef = collection(db, 'snippets');
      const q = query(snippetsRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const snippets = [];
      querySnapshot.forEach((docSnap) => {
        snippets.push({ id: docSnap.id, ...docSnap.data() });
      });
      const dataStr = JSON.stringify(snippets, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', 'snipit_account_backup.json');
      linkElement.click();
    } catch (error) {
      console.error("Error exporting data: ", error);
      alert('Failed to export data.');
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to completely delete your account? This will permanently erase all your snippets, profile data, and settings. This action CANNOT be undone.")) {
      try {
        if (!currentUser) return;
        const uid = currentUser.uid;
        
        // Delete all snippets
        const snippetsRef = collection(db, 'snippets');
        const q = query(snippetsRef, where('userId', '==', uid));
        const querySnapshot = await getDocs(q);
        const deletePromises = [];
        querySnapshot.forEach((docSnap) => {
          deletePromises.push(deleteDoc(docSnap.ref));
        });
        await Promise.all(deletePromises);
        
        // Delete User Account
        await deleteUser(currentUser);
        alert("Account successfully deleted.");
      } catch (error) {
        console.error("Error deleting account: ", error);
        if (error.code === 'auth/requires-recent-login') {
          alert('For security reasons, you must log out and log back in before deleting your account.');
        } else {
          alert('Failed to delete account. ' + error.message);
        }
      }
    }
  };

  const isMfaEnrolled = currentUser?.multiFactor?.enrolledFactors?.length > 0;

  return (
    <div className="p-8 h-full overflow-y-auto">
      <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
      <p className="text-gray-400 mb-8">Manage your account and organization preferences.</p>

      <div className="flex space-x-8">
        {/* Settings Navigation */}
        <div className="w-64 space-y-2">
          <button 
            onClick={() => setActiveTab('account')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'account' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
          >
            <User className="w-5 h-5" />
            <span className="font-medium">Account Profile</span>
          </button>
          <button 
            onClick={() => setActiveTab('org')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'org' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
          >
            <Building className="w-5 h-5" />
            <span className="font-medium">Organization</span>
          </button>
          <button 
            onClick={() => setActiveTab('security')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'security' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
          >
            <Shield className="w-5 h-5" />
            <span className="font-medium">Security</span>
          </button>
          <button 
            onClick={() => setActiveTab('billing')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'billing' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">Billing</span>
          </button>
          {userProfile?.isAdmin && (
            <button 
              onClick={() => setActiveTab('admin')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'admin' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
            >
              <Shield className="w-5 h-5" />
              <span className="font-medium">Admin</span>
            </button>
          )}
        </div>

        {/* Settings Content */}
        <div className="flex-1 max-w-5xl">
          {activeTab === 'account' && (
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 space-y-6">
              <h2 className="text-xl font-bold text-white mb-4">Account Profile</h2>
              
              <div className="flex items-center space-x-4 mb-6">
                {photoURL ? (
                  <OptimizedImage src={photoURL} alt="Profile" className="w-16 h-16 rounded-full border border-gray-700" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center text-xl font-bold">
                    {fullName.charAt(0) || 'U'}
                  </div>
                )}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-400 mb-1">Avatar Image URL</label>
                  <input type="text" value={photoURL} onChange={(e) => setPhotoURL(e.target.value)} placeholder="https://example.com/avatar.png" className="w-full bg-[#121212] border border-gray-800 rounded p-2 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                  <input type="email" value={userProfile?.email || ''} disabled className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-gray-500 cursor-not-allowed" />
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Organization</label>
                    <input type="text" value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="Company Name" className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Role</label>
                    <input type="text" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Software Engineer" className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Bio</label>
                  <textarea rows="4" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell us about yourself..." className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-blue-500 focus:outline-none"></textarea>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800 flex justify-between items-center">
                <button onClick={handleExportData} className="text-blue-400 hover:text-blue-300 font-medium">Export My Data</button>
                <button onClick={handleSaveAccount} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">Save Changes</button>
              </div>
            </div>
          )}

          {activeTab === 'org' && (
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 space-y-6">
              <h2 className="text-xl font-bold text-white mb-4">Organization Settings</h2>
              
              {enterpriseOrg ? (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Enterprise Workspace Name</label>
                  <input 
                    type="text" 
                    value={enterpriseOrg.name} 
                    disabled 
                    className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-gray-300 cursor-not-allowed mb-4" 
                  />
                  <p className="text-sm text-gray-400">
                    To manage members and teams, please visit the <button onClick={() => navigate('/org')} className="text-blue-400 hover:text-blue-300 underline">Organization Dashboard</button>.
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-4 border border-yellow-900/50 bg-yellow-900/10 rounded-lg">
                    <p className="text-yellow-500 text-sm">Organization features require an Enterprise license.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Workspace Name</label>
                    <input type="text" placeholder="My Company" disabled className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-gray-500 cursor-not-allowed mb-4" />
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 space-y-6">
              <h2 className="text-xl font-bold text-white mb-4">Security</h2>
              
              <div className="border border-gray-800 rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Key className="w-4 h-4 text-blue-400" />
                      Two-Factor Authentication (2FA)
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Protect your account with an additional layer of security.
                    </p>
                  </div>
                  <div>
                    {isMfaEnrolled ? (
                      <span className="bg-green-900/30 text-green-400 px-3 py-1 rounded text-sm border border-green-900/60 font-medium">
                        Enabled
                      </span>
                    ) : (
                      <span className="bg-gray-800 text-gray-400 px-3 py-1 rounded text-sm border border-gray-700 font-medium">
                        Disabled
                      </span>
                    )}
                  </div>
                </div>

                {!isMfaEnrolled && !enrollingMfa && (
                  <button 
                    onClick={() => setEnrollingMfa(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                  >
                    Set up SMS 2FA
                  </button>
                )}

                {enrollingMfa && (
                  <div className="mt-4 p-4 bg-[#121212] rounded border border-gray-800 space-y-4">
                    <h4 className="text-white font-medium text-sm">Enroll Phone Number</h4>
                    
                    {mfaError && (
                      <div className="text-red-400 text-sm bg-red-900/20 p-2 rounded border border-red-900/40">
                        {mfaError}
                      </div>
                    )}

                    {!mfaVerificationId ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">Phone Number (with country code)</label>
                          <input 
                            type="text" 
                            placeholder="+1234567890" 
                            value={mfaPhone}
                            onChange={(e) => setMfaPhone(e.target.value)}
                            className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white text-sm focus:border-blue-500 focus:outline-none" 
                          />
                        </div>
                        <div className="flex gap-2">
                          <button 
                            id="mfa-enroll-button"
                            onClick={handleStartMfa}
                            disabled={mfaLoading || !mfaPhone}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {mfaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                            Send Code
                          </button>
                          <button 
                            onClick={() => setEnrollingMfa(false)}
                            className="text-gray-400 hover:text-white px-4 py-2 rounded text-sm font-medium transition-colors border border-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">6-Digit SMS Code</label>
                          <input 
                            type="text" 
                            placeholder="000000" 
                            value={mfaSmsCode}
                            onChange={(e) => setMfaSmsCode(e.target.value)}
                            maxLength={6}
                            className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-center tracking-[0.5em] font-mono text-white focus:border-blue-500 focus:outline-none" 
                          />
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={handleCompleteMfa}
                            disabled={mfaLoading || mfaSmsCode.length < 6}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {mfaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                          </button>
                          <button 
                            onClick={() => setMfaVerificationId('')}
                            className="text-gray-400 hover:text-white px-4 py-2 rounded text-sm font-medium transition-colors border border-gray-700"
                          >
                            Back
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-800">
                <button onClick={handleDeleteAccount} className="text-red-400 hover:text-red-300 font-medium">Delete Account</button>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <BillingView isEmbedded={true} />
          )}

          {activeTab === 'admin' && (
            <AdminView isEmbedded={true} />
          )}
        </div>
      </div>
    </div>
  );
}
